/**
 * MainPage — new customer home (multi-vertical marketplace landing).
 *
 * Layout:
 *   • CustomerHeader (address + search)
 *   • Banner carousel
 *   • 4 prominent hero tiles (tenant-configured)
 *   • "Discover More" horizontal categories carousel with "View all" CTA
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { ChevronRight, Search } from "lucide-react";
import { poweredByBrand } from "../../lib/utils";
import { api } from "../../lib/api";
import { useTenant } from "../../contexts/TenantContext";
import CustomerHeader from "../../components/CustomerHeader";
import BannerCarousel from "../../components/BannerCarousel";
import MarketplaceHome from "../../components/MarketplaceHome";
import ReferralBanner from "../../components/ReferralBanner";
import VoiceSearch from "../../components/VoiceSearch";
import BizCategoryIcon from "../../components/BizCategoryIcon";
import { THEME_PRESETS, resolveBrowseMode } from "../../lib/themes";
import { t } from "../../lib/i18n";
import LseHome from "../../components/lse/LseHome";

export default function MainPage() {
    const { lang, tenant } = useTenant();
    const [tiles, setTiles] = useState([]);
    const [categories, setCategories] = useState([]);
    const [banners, setBanners] = useState([]);
    const [feed, setFeed] = useState(null);
    // iter104 — Single-vendor mode auto-routes the customer straight
    // into the tenant's one and only shop. The backend surfaces the
    // shop id inline on the tenant doc (`tenant.single_vendor_shop_id`)
    // so we redirect immediately with no extra API call. When the
    // tenant is flipped to single-vendor BEFORE its shop is created,
    // we fail open and show the legacy layout — the tenant admin can
    // add a shop from Admin/Merchant consoles either way.
    const isSingleVendor = (tenant?.vendor_mode || "MULTI_VENDOR") === "SINGLE_VENDOR";
    const singleVendorShopId = isSingleVendor ? tenant?.single_vendor_shop_id : null;
    const navigate = useNavigate();

    // iter114 — Discover-More section (horizontal categories carousel
    // below the hero tiles) can be hidden per tenant. Defaults ON so
    // existing tenants are unaffected until an admin toggles it off.
    const discoveryVisible = tenant?.branding?.discovery_mode_visible !== false;

    // iter56 — if the tenant picked a marketplace theme (Amazon /
    // Flipkart / Blinkit), the entire ``/`` route hands over to the
    // product-first storefront. Hero tiles + Discover-More are skipped.
    const presetId = tenant?.branding?.theme_preset;
    const marketLayout = presetId ? THEME_PRESETS[presetId]?.homeLayout : null;

    // iter83 — When the tenant disables the home-tiles landing page,
    // route the visitor straight to the theme's default browse view.
    // Marketplace themes (which set ``homeLayout``) keep their existing
    // product-first home — the toggle only affects the legacy 4-tile
    // route. ``home_tiles_enabled`` defaults to ``true`` for legacy
    // tenants so behaviour is backwards-compatible. We resolve the
    // redirect target up-front but render the <Navigate> only AFTER
    // every hook has run (React rules-of-hooks).
    const homeTilesEnabled = tenant?.branding?.home_tiles_enabled !== false;
    const shouldRedirect = !homeTilesEnabled && !marketLayout;
    const redirectTarget = shouldRedirect
        ? (resolveBrowseMode({
            tenantMode: tenant?.branding?.default_browse_mode,
            presetId,
        }) === "PRODUCTS" ? "/products" : "/food")
        : null;

    useEffect(() => {
        // iter104 — single-vendor tenants: no data-fetch needed here,
        // we redirect straight into the shop below. All catalog fetches
        // are skipped.
        if (isSingleVendor && singleVendorShopId) return;
        // Bail out of the data fetches when we're about to redirect —
        // saves a wasted API call on the brief render that precedes
        // <Navigate>.
        if (shouldRedirect) return;
        if (marketLayout) {
            // Marketplace mode needs verticals + feed but skips hero tiles.
            api.get("/business-categories").then(({ data }) =>
                setCategories((data || []).filter((c) => !c.parent_id)),
            );
            api.get("/feed").then(({ data }) => setFeed(data || {})).catch(() => setFeed({}));
            return;
        }
        api.get("/hero-tiles").then(({ data }) => setTiles(data || []));
        // iter114 — skip the categories fetch entirely when the
        // Discover-More section is hidden. No wasted request, no
        // stale state left over.
        if (discoveryVisible) {
            api.get("/business-categories").then(({ data }) =>
                setCategories((data || []).filter((c) => !c.parent_id)),
            );
        }
        // Reuse the existing /feed endpoint just for its banners (already zone-filtered).
        api.get("/feed").then(({ data }) => setBanners(data?.banners || [])).catch(() => {});
    }, [marketLayout, shouldRedirect, isSingleVendor, singleVendorShopId, discoveryVisible]);

    // iter104 — single-vendor redirect takes priority over every other
    // route the customer landing page might send them to.
    if (isSingleVendor && singleVendorShopId) {
        return <Navigate to={`/restaurant/${singleVendorShopId}`} replace />;
    }

    if (shouldRedirect) {
        return <Navigate to={redirectTarget} replace />;
    }

    if (marketLayout) {
        return (
            <MarketplaceHome
                variant={marketLayout}
                feed={feed}
                verticals={categories}
                tenant={tenant}
            />
        );
    }

    const goToTile = (tile) => {
        if (tile.cta_type === "EXTERNAL") {
            window.open(tile.cta_target, "_blank", "noopener");
            return;
        }
        // iter83 — resolve effective browse mode (per-tile override →
        // tenant default → theme preset). For CATEGORY tiles we route
        // to /c/{slug} (Shops) or /c/{slug}/products (Products); for
        // ROUTE tiles we just follow the explicit target since the
        // admin authored an exact path.
        const mode = resolveBrowseMode({
            tileMode: tile.landing_mode,
            tenantMode: tenant?.branding?.default_browse_mode,
            presetId,
        });
        if (tile.cta_type === "CATEGORY") {
            const slug = tile.cta_target;
            navigate(mode === "PRODUCTS" ? `/c/${slug}/products` : `/c/${slug}`);
            return;
        }
        // ROUTE tile — honour the admin-set path, but if it's the
        // generic "/food" placeholder upgrade it to /products when the
        // resolved mode is PRODUCTS (so tile authors don't have to
        // remember two paths for the same intent).
        if (tile.cta_target === "/food" && mode === "PRODUCTS") {
            navigate("/products");
            return;
        }
        navigate(tile.cta_target || "/food");
    };

    return (
        <div className="pb-32" data-testid="main-page">
            <CustomerHeader>
                <Link to="/search" className="flex items-center justify-between bg-muted rounded-xl px-4 py-3 group active:scale-[0.98] transition">
                    <div className="flex items-center gap-3 text-muted-foreground">
                        <Search size={18} />
                        <span className="text-sm">{t("search_placeholder", lang)}</span>
                    </div>
                    <VoiceSearch />
                </Link>
            </CustomerHeader>

            {/* Referral nudge — only renders for active referrers (auth + linked shops or earnings today). */}
            <ReferralBanner />

            {/* Banner carousel */}
            <div className="px-4 mt-4"><BannerCarousel banners={banners} /></div>

            {/* Location Service Engine tenants only (renders nothing otherwise). */}
            <LseHome />

            {/* Hero tiles — wrap responsively, no hard 4-tile cap. Admins
                can author up to ``MAX_TILES`` (12) so power tenants with
                many verticals can surface them all without rebuilding the
                grid. Phones show 2 per row, tablets 3, desktops 4. */}
            <section className="px-4 mt-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="hero-tiles-grid">
                {tiles.slice(0, 12).map((tile) => (
                    <button
                        key={tile.id}
                        data-testid={`hero-tile-${tile.position}`}
                        onClick={() => goToTile(tile)}
                        className="relative text-left bg-card border border-border rounded-2xl p-3 overflow-hidden active:scale-[0.98] transition group min-h-[124px]"
                    >
                        <div className="text-[13px] font-extrabold leading-tight uppercase tracking-wide pr-2 line-clamp-2">{tile.title}</div>
                        {tile.subtitle && (
                            <div className="text-[11px] text-muted-foreground mt-0.5 line-clamp-1">{tile.subtitle}</div>
                        )}
                        {tile.status_label && (
                            <span
                                className="inline-block mt-2 text-[10px] font-bold px-2 py-0.5 rounded-full text-white"
                                style={{ background: tile.status_color || "#10B981" }}
                            >
                                {tile.status_label}
                            </span>
                        )}
                        {tile.image_url && (
                            <img
                                src={tile.image_url}
                                alt=""
                                className="absolute right-1.5 bottom-1.5 w-16 h-16 object-contain pointer-events-none mix-blend-multiply"
                                style={{ filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.08))" }}
                                loading="lazy"
                                onError={(e) => { e.currentTarget.style.display = "none"; }}
                            />
                        )}
                    </button>
                ))}
            </section>

            {/* Discover More — horizontal categories. iter114: hidden
                when the tenant admin turns off `discovery_mode_visible`
                in Admin → Branding. */}
            {discoveryVisible && (
            <section className="mt-5" data-testid="discover-more">
                <div className="flex items-center justify-between px-4 mb-2">
                    <h3 className="text-sm font-extrabold uppercase tracking-wide">Discover more</h3>
                    <Link to="/categories" data-testid="view-all-categories" className="text-xs font-bold text-primary inline-flex items-center gap-0.5">
                        View all <ChevronRight size={14} />
                    </Link>
                </div>
                <div className="flex gap-2.5 overflow-x-auto px-4 no-scrollbar pb-2">
                    {categories.map((c) => {
                        // iter83 — honour tenant default browse mode for
                        // discover-more category chips so the experience
                        // is consistent regardless of which surface the
                        // customer taps from.
                        const mode = resolveBrowseMode({
                            tenantMode: tenant?.branding?.default_browse_mode,
                            presetId,
                        });
                        const to = c.slug === "food" && mode === "SHOPS"
                            ? "/food"
                            : mode === "PRODUCTS"
                                ? `/c/${c.slug}/products`
                                : `/c/${c.slug}`;
                        return (
                            <Link
                                key={c.id}
                                to={to}
                                data-testid={`discover-cat-${c.slug}`}
                                className="flex-shrink-0 w-[72px] flex flex-col items-center text-center"
                            >
                                <div className="w-[64px] h-[64px] rounded-2xl bg-amber-400 flex items-center justify-center mb-1.5 shadow-sm active:scale-95 transition">
                                    <BizCategoryIcon name={c.icon} size={26} strokeWidth={2.4} className="text-foreground" />
                                </div>
                                <div className="text-[11px] font-semibold leading-tight">{c.name}</div>
                            </Link>
                        );
                    })}
                </div>
            </section>
            )}

            <footer className="text-center py-8" data-testid="home-footer">
                <p className="text-sm font-semibold text-foreground/80">{tenant?.name}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                    Powered by {poweredByBrand(tenant?.branding?.powered_by_text)}
                </p>
            </footer>
        </div>
    );
}
